/**
 * Draper's brain is pluggable per workspace.
 *
 *  - "gemini"        : built-in, replies synchronously (the original behaviour).
 *  - "claude_worker" : chat messages are queued for an external Claude worker (a cloud
 *                      routine) that replies asynchronously, usually within ~1-5 minutes.
 *  - "hybrid"        : Gemini answers chat instantly; production work (articles, posts, plans)
 *                      is handed to the Claude team through a `delegate_to_claude` tool, which
 *                      creates a `work` job. When the job finishes the server appends a
 *                      "Peggy finished: ..." note to the Draper thread and the draft lands under
 *                      "Needs your review". Chat itself is never queued, so hybrid sends chat
 *                      exactly like gemini.
 *
 * These types mirror the server contract for the queued flow:
 *   POST /api/conversations/:id/chat           -> QueuedChatResponse (instead of a reply)
 *   GET  /api/conversations/:id/jobs/:jobId    -> ChatJobResponse
 *   GET  /api/workers/status                   -> WorkersStatusResponse
 */
import type { Workspace } from "@shared/schema";

export type AiEngine = "gemini" | "claude_worker" | "hybrid";

/** Workspace as returned by the API once the server adds `aiEngine` (optional so older payloads still type-check). */
export type WorkspaceWithEngine = Workspace & { aiEngine?: AiEngine | null };

/** Safe read of a workspace's engine; anything that isn't explicitly "claude_worker" or "hybrid" behaves as Gemini. */
export function getAiEngine(workspace: unknown): AiEngine {
  const engine = (workspace as { aiEngine?: unknown } | null | undefined)?.aiEngine;
  return engine === "claude_worker" || engine === "hybrid" ? engine : "gemini";
}

/**
 * True when the Claude team does the production work (claude_worker and hybrid). These workspaces get the
 * simple Content Studio (review inbox) and must not trigger Gemini-era side effects when a screen is merely opened.
 */
export function usesClaudeTeam(engine: AiEngine): boolean {
  return engine === "claude_worker" || engine === "hybrid";
}

/** True only when every chat message is queued for the worker (claude_worker). Hybrid chat is instant. */
export function queuesChat(engine: AiEngine): boolean {
  return engine === "claude_worker";
}

// ── Queued chat ──────────────────────────────────────────────────────────────

/** Returned by a chat POST when the engine is claude_worker. */
export interface QueuedChatResponse {
  queued: true;
  jobId: string | number;
  conversationId: number | string;
  engine: "claude_worker";
}

/** Type guard for the queued response. Requires the ids needed to poll the job. */
export function isQueuedChatResponse(value: unknown): value is QueuedChatResponse {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  const idOk = (x: unknown) => typeof x === "string" || typeof x === "number";
  return v.queued === true && idOk(v.jobId) && idOk(v.conversationId);
}

export type ChatJobStatus = "queued" | "claimed" | "done" | "failed";

/** GET /api/conversations/:id/jobs/:jobId */
export interface ChatJobResponse {
  id: string | number;
  status: ChatJobStatus;
  result: { text: string } | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

/** How often an in-flight job is polled, and when we stop and offer a retry. */
export const JOB_POLL_INTERVAL_MS = 5_000;
export const JOB_TIMEOUT_MS = 15 * 60_000;

// ── Worker status ────────────────────────────────────────────────────────────

export interface WorkerInfo {
  workerId: string;
  lastSeenAt: string | null;
  note?: string | null;
  runs?: number;
}

/** GET /api/workers/status?workspaceId=  (queued / claimed now include `work` jobs, not just chat). */
export interface WorkersStatusResponse {
  engine: AiEngine;
  workers: WorkerInfo[];
  queued: number;
  claimed: number;
}

/** never = no worker has ever checked in; stale = last seen > 10 min ago; fresh = seen within 10 min. */
export type WorkerHealth = "never" | "stale" | "fresh";

export const WORKER_STATUS_POLL_MS = 30_000;
/** While work is queued or claimed, poll faster so the "Work in progress" strip clears soon after the team finishes. */
export const WORKER_STATUS_BUSY_POLL_MS = 10_000;
export const WORKER_FRESH_MS = 10 * 60_000;

/** Most recent check-in across all workers (ignores unparseable timestamps). */
export function latestWorkerSeenAt(status: WorkersStatusResponse | null | undefined): string | null {
  let best: { iso: string; t: number } | null = null;
  for (const w of status?.workers ?? []) {
    if (!w?.lastSeenAt) continue;
    const t = new Date(w.lastSeenAt).getTime();
    if (Number.isNaN(t)) continue;
    if (!best || t > best.t) best = { iso: w.lastSeenAt, t };
  }
  return best?.iso ?? null;
}

export function getWorkerHealth(lastSeenAt: string | null, now: number = Date.now()): WorkerHealth {
  if (!lastSeenAt) return "never";
  const t = new Date(lastSeenAt).getTime();
  if (Number.isNaN(t)) return "never";
  return now - t > WORKER_FRESH_MS ? "stale" : "fresh";
}

/** Compact relative time: "just now", "5m ago", "3h ago", "2d ago". */
export function formatTimeAgo(iso: string | number | null | undefined, now: number = Date.now()): string {
  if (iso === null || iso === undefined || iso === "") return "never";
  const t = typeof iso === "number" ? iso : new Date(iso).getTime();
  if (Number.isNaN(t)) return "recently";
  const secs = Math.max(0, Math.floor((now - t) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
